import type { ReactNode } from 'react';
import { useEffect } from 'react';

import _ from '@luna/lodash';

import type { TypedFieldName } from './type';
import { useWatchFieldValue } from './useWatchFieldValue';
import { useFormContext } from './useFormContext';

interface Props<FormFields, FormFieldType> {
    /**
     * Name of the FormField that depends on the rendering of the children content.
     */
    conditionFieldName: TypedFieldName<FormFields>;

    /**
     * The content that will be rendered if one of the `conditionValues` is matched against `conditionFieldName` FormField value
     * or when the condition predicate returns true.
     */
    children?: ReactNode;

    /**
     * Values against which the rendering of `children` content is evaluated.
     * The children content will only be rendered if one of the `conditionValues` is matched against the value of `conditionFieldName` FormField.
     */
    conditionValues: FormFieldType[];

    /**
     * A list of all the fields that needs to be reset.
     */
    fieldsToReset?: TypedFieldName<FormFields>[];

    /**
     * Controls how the conditionValues array is interpreted to decide when to reset the fields; defaults to resetOnMatch.
     */
    resetMode?: 'resetOnMatch' | 'resetOnMismatch';
}

export const ShowOnFieldConditionMatch = <FormFields = void, FormFieldType = boolean>(
    props: Props<FormFields, FormFieldType>,
): ReactNode => {
    const {
        conditionFieldName,
        children,
        conditionValues,
        fieldsToReset = [],
        resetMode = 'resetOnMatch',
    } = props;
    const value = useWatchFieldValue<FormFieldType, FormFields>(conditionFieldName);
    const { unregister } = useFormContext();

    const isMatching = conditionValues.some((conditionValue) => _.isEqual(conditionValue, value));
    const shouldReset = resetMode === 'resetOnMatch' ? !isMatching : isMatching;

    useEffect(() => {
        if (shouldReset) {
            fieldsToReset.forEach((field) => unregister(field, { keepDefaultValue: true }));
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...conditionValues, ...fieldsToReset, value, resetMode, shouldReset]);

    return shouldReset ? null : children;
};
